// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Documentation Archive
// Unified document repository for all supporting documentation:
// ticket images, truck certs, invoices, regulatory docs, etc.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Modal, Stack, SimpleGrid, Textarea,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconUpload, IconTrash, IconEye, IconDownload,
  IconFile, IconFileText, IconPhoto, IconCertificate,
  IconFileInvoice, IconShieldCheck, IconBook, IconSchool,
  IconRefresh, IconX, IconFilter, IconClipboardList,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debris-store';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Categories ──

const CATEGORIES = [
  { value: 'load_tickets', label: 'Load Ticket PDFs', color: '#0285C4', icon: IconFile },
  { value: 'contractor_invoice', label: 'Contractor Invoice', color: '#C68700', icon: IconFileInvoice },
  { value: 'invoice', label: 'Invoice', color: '#A627FF', icon: IconFileInvoice },
  { value: 'truck_certification', label: 'Truck Certification', color: 'var(--color-success)', icon: IconCertificate },
  { value: 'truck_cert', label: 'Truck Cert (Legacy)', color: 'var(--color-success)', icon: IconCertificate },
  { value: 'sow_work_order', label: 'Scope of Work', color: '#7C3AED', icon: IconFileText },
  { value: 'inspection_report', label: 'Inspection Report', color: '#059669', icon: IconShieldCheck },
  { value: 'checklist_evidence', label: 'Checklist Docs', color: '#4B3FFF', icon: IconClipboardList },
  { value: 'supporting_evidence', label: 'Supporting Docs', color: '#03AC9E', icon: IconFile },
  { value: 'ticket_image', label: 'Ticket Image', color: '#0285C4', icon: IconPhoto },
  { value: 'reconciliation', label: 'Reconciliation', color: '#3b82f6', icon: IconFileText },
  { value: 'fa_support', label: 'FA Support Docs', color: '#0E7490', icon: IconClipboardList },
  { value: 'transaction_detail', label: 'Transaction Detail', color: '#6366f1', icon: IconFileText },
  { value: 'other', label: 'Other', color: '#546574', icon: IconFile },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

const LINKED_TO_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ticket', label: 'Tickets' },
  { value: 'invoice', label: 'Invoices' },
  { value: 'fa_record', label: 'FA Records' },
  { value: 'sub_invoice', label: 'Sub Invoices' },
];

function getCategoryInfo(cat: string) {
  return CATEGORY_MAP[cat] || { label: cat || 'Other', color: '#546574', icon: IconFile };
}

// ── Auto-classify by filename ──

function autoClassify(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.includes('truck') || lower.includes('cert')) return 'truck_cert';
  if (lower.includes('invoice') || lower.includes('inv_')) return 'invoice';
  if (lower.includes('ticket') || lower.includes('load')) return 'ticket_image';
  if (lower.includes('reconcil')) return 'reconciliation';
  if (lower.includes('timesheet') || lower.includes('force_account') || lower.includes('fa_') || lower.includes('annex')) return 'fa_support';
  if (lower.includes('regulat') || lower.includes('permit') || lower.includes('fema')) return 'regulatory';
  if (lower.includes('train')) return 'training';
  if (lower.includes('reference') || lower.includes('guide') || lower.includes('manual')) return 'reference';
  return 'other';
}

// ── Helpers ──

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ════════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════════

export default function EvidenceArchive() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident, user } = useAuthStore();

  // Store
  const {
    documents, loadDocuments, loadingDocs,
    createDocument, uploadDocumentFile, getDocumentDownloadUrl, deleteDocument, updateDocument,
    contractors, fetchContractors,
  } = useDebrisStore();

  // Invoices for filters
  const { invoices } = useDebrisOpsStore();

  // Local state (must be before memos that reference them)
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [contractorFilter, setContractorFilter] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('');

  // Extract linked invoice ID from document notes
  const getLinkedInvoiceId = (doc: any): string => {
    const notes = doc.notes || doc.description || '';
    const m = /\|\|\|INVOICELINK\|\|\|.*?"invoiceId"\s*:\s*"([^"]+)"/.exec(notes);
    return m?.[1] || '';
  };

  // Lookup maps + filter options (after state declarations)
  const contractorLookup = useMemo(() => {
    const m = new Map<string, string>();
    contractors.forEach(c => m.set(String(c.id), c.companyName || `Contractor #${c.id}`));
    return m;
  }, [contractors]);
  const invoiceLookup = useMemo(() => {
    const m = new Map<string, { number: string; contractorId: string }>();
    invoices.forEach(inv => m.set(String(inv.id), {
      number: inv.invoiceNumber || `#${inv.id}`,
      contractorId: inv.contractorId || '',
    }));
    return m;
  }, [invoices]);
  const contractorFilterOptions = useMemo(() => [
    { value: '', label: 'All Contractors' },
    ...contractors.map(c => ({ value: String(c.id), label: c.companyName || `Contractor #${c.id}` })),
  ], [contractors]);
  const invoiceFilterOptions = useMemo(() => {
    let invs = invoices;
    if (contractorFilter) invs = invs.filter(inv => inv.contractorId === contractorFilter);
    return [
      { value: '', label: 'All Invoices' },
      ...invs.map(inv => {
        const cName = contractorLookup.get(inv.contractorId) || '';
        return { value: String(inv.id), label: `${inv.invoiceNumber || '#' + inv.id}${cName ? ' — ' + cName : ''}` };
      }),
    ];
  }, [invoices, contractorFilter, contractorLookup]);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  // PDF loading
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load data
  useEffect(() => {
    loadDocuments();
    fetchContractors();
  }, []);

  // Category filter options
  const categoryOptions = useMemo(() => [
    { value: '', label: 'All Categories' },
    ...CATEGORIES.map(c => ({ value: c.value, label: c.label })),
  ], []);

  // Filter + sort
  const filtered = useMemo(() => {
    let result = [...documents];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q) ||
        (d.uploadedBy || '').toLowerCase().includes(q)
      );
    }
    if (categoryFilter) {
      result = result.filter(d => d.documentType === categoryFilter || d.category === categoryFilter);
    }
    if (contractorFilter) {
      // Find all invoice IDs for this contractor, then filter docs linked to those invoices
      const contractorInvIds = new Set(invoices.filter(inv => inv.contractorId === contractorFilter).map(inv => String(inv.id)));
      result = result.filter(d => {
        const invId = getLinkedInvoiceId(d);
        return invId && contractorInvIds.has(invId);
      });
    }
    if (invoiceFilter) {
      result = result.filter(d => getLinkedInvoiceId(d) === invoiceFilter);
    }
    return result;
  }, [documents, search, categoryFilter, contractorFilter, invoiceFilter, invoices]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [filtered, sortField, sortDir]);

  // Stats
  const stats = useMemo(() => {
    const counts: Record<string, number> = {};
    CATEGORIES.forEach(c => { counts[c.value] = 0; });
    documents.forEach(d => {
      const cat = d.documentType || d.category || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return { total: documents.length, ...counts };
  }, [documents]);

  // Sort toggle
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortIcon = (col: string) =>
    sortField === col ? (sortDir === 'desc' ? '↓' : '↑') : <span style={{ opacity: 0.25 }}>↑</span>;

  const thStyle = (col: string): React.CSSProperties => ({
    cursor: 'pointer', userSelect: 'none',
    color: sortField === col ? '#4B3FFF' : '#64748b',
    padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600,
    whiteSpace: 'nowrap',
  });

  // ── Upload logic ──

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadFiles(files);
    // Auto-classify first file if category not set
    if (!uploadCategory && files.length > 0) {
      setUploadCategory(autoClassify(files[0].name));
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    const cat = uploadCategory || 'other';
    setUploading(true);
    setUploadProgress({ done: 0, total: uploadFiles.length });

    const incidentId = activeIncident?.id?.toString() ?? '';
    const uploadedBy = (user as any)?.email || (user as any)?.name || '';

    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      try {
        // Create document record
        const docId = await createDocument({
          title: file.name,
          documentType: cat,
          incidentId,
          uploadedBy,
          notes: '',
        });
        if (docId) {
          // Upload file to S3
          await uploadDocumentFile(docId, file);
        }
      } catch (e) {
        console.error('[Evidence] upload failed for', file.name, e);
        notifications.show({ title: 'Upload failed', message: file.name, color: 'red' });
      }
      setUploadProgress({ done: i + 1, total: uploadFiles.length });
    }

    setUploading(false);
    setShowUpload(false);
    setUploadFiles([]);
    setUploadCategory('');
    notifications.show({ title: 'Upload complete', message: `${uploadFiles.length} document(s) uploaded`, color: 'green' });
  };

  // ── View / download ──

  const handleView = async (doc: any) => {
    if (!doc.fileId) {
      notifications.show({ title: 'No file', message: 'This document has no file attached', color: 'orange' });
      return;
    }
    setPdfLoading(doc.fileId);
    try {
      const url = await getDocumentDownloadUrl(doc.fileId);
      setPdfViewerUrl(`/api/filepreview?url=${encodeURIComponent(url)}`);
      setPdfViewerTitle(doc.title || 'Document Viewer');
    } catch {
      notifications.show({ title: 'Error', message: 'Could not retrieve file URL', color: 'red' });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteDocument(deleteTarget.id);
    setDeleteTarget(null);
    selectedIds.delete(deleteTarget.id);
    setSelectedIds(new Set(selectedIds));
    notifications.show({ title: 'Deleted', message: 'Document removed', color: 'green' });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    let deleted = 0;
    for (const id of selectedIds) {
      try { await deleteDocument(id); deleted++; } catch {}
    }
    setSelectedIds(new Set());
    setBulkDeleting(false);
    notifications.show({ message: `${deleted} document(s) deleted`, color: 'var(--color-success)' });
  };

  const handleSaveNotes = async () => {
    if (!selectedDoc) return;
    await updateDocument(selectedDoc.id, { notes: editNotes });
    setSelectedDoc(null);
    notifications.show({ title: 'Saved', message: 'Notes updated', color: 'green' });
  };

  // ════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════

  return (
    <Stack gap={12}>
      <Group style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Title order={3}>Documentation Archive</Title>
        <Group gap={8}>
          {selectedIds.size > 0 && canEdit && (
            <Button color="var(--color-error)" variant="light" size="sm"
              leftSection={<IconTrash size={14} />}
              loading={bulkDeleting}
              onClick={handleBulkDelete}>
              Delete {selectedIds.size} Selected
            </Button>
          )}
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={loadDocuments}>
            Refresh
          </Button>
          {canEdit && (
            <Button leftSection={<IconUpload size={16} />} onClick={() => setShowUpload(true)}>
              Upload Documents
            </Button>
          )}
        </Group>
      </Group>

      {/* Search + Filter */}
      <Group gap={8}>
        <TextInput
          placeholder="Search documents..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Select
          data={categoryOptions}
          value={categoryFilter}
          onChange={(v: string | null) => setCategoryFilter(v || '')}
          placeholder="Filter by category"
          style={{ width: 220 }}
        />
        <Select
          data={contractorFilterOptions}
          value={contractorFilter}
          onChange={(v: string | null) => { setContractorFilter(v || ''); setInvoiceFilter(''); }}
          placeholder="Contractor..."
          searchable clearable
          style={{ width: 200 }}
        />
        <Select
          data={invoiceFilterOptions}
          value={invoiceFilter}
          onChange={(v: string | null) => setInvoiceFilter(v || '')}
          placeholder="Invoice..."
          searchable clearable
          style={{ width: 220 }}
        />
        {(search || categoryFilter || contractorFilter || invoiceFilter) && (
          <Button variant="subtle" size="xs" onClick={() => { setSearch(''); setCategoryFilter(''); setContractorFilter(''); setInvoiceFilter(''); }}>
            Clear
          </Button>
        )}
      </Group>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 12 }}>
        <Paper withBorder p="sm" style={{ borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ color: '#4B3FFF', marginBottom: 2 }}><IconFile size={18} /></div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#4B3FFF' }}>{stats.total}</div>
          <Text size="xs" c="dimmed">Total</Text>
        </Paper>
        {CATEGORIES.slice(0, 6).map(cat => (
          <Paper key={cat.value} withBorder p="sm" style={{
            borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', cursor: 'pointer',
            border: categoryFilter === cat.value ? `2px solid ${cat.color}` : undefined,
          }}
            onClick={() => setCategoryFilter(categoryFilter === cat.value ? '' : cat.value)}
          >
            <div style={{ color: cat.color, marginBottom: 2 }}><cat.icon size={18} /></div>
            <div style={{ fontSize: 28, fontWeight: 800, color: cat.color }}>{(stats as any)[cat.value] || 0}</div>
            <Text size="xs" c="dimmed">{cat.label}</Text>
          </Paper>
        ))}
      </div>

      {/* Table */}
      <Paper withBorder p={0} style={{ borderRadius: 8 }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 440px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
              <tr>
                {canEdit && (
                  <th style={{ padding: '10px 8px', width: 36 }}>
                    <input type="checkbox"
                      checked={sorted.length > 0 && selectedIds.size === sorted.length}
                      onChange={e => {
                        if (e.target.checked) setSelectedIds(new Set(sorted.map(d => d.id)));
                        else setSelectedIds(new Set());
                      }}
                      style={{ width: 15, height: 15, accentColor: '#4B3FFF', cursor: 'pointer' }}
                    />
                  </th>
                )}
                {[
                  { key: 'title', label: 'Document' },
                  { key: 'documentType', label: 'Type' },
                  { key: 'linkedInvoice', label: 'Linked Invoice' },
                  { key: 'uploadedBy', label: 'Uploaded By' },
                ].map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)} style={thStyle(col.key)}>
                    {col.label} {sortIcon(col.key)}
                  </th>
                ))}
                <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#64748b' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={canEdit ? 6 : 5} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                  {loadingDocs ? 'Loading documents...' : 'No documents found'}
                </td></tr>
              )}
              {sorted.map(doc => {
                const catInfo = getCategoryInfo(doc.documentType || doc.category);
                const isSelected = selectedIds.has(doc.id);
                return (
                  <tr
                    key={doc.id}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: isSelected ? '#ede9fe' : undefined }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ''; }}
                    onClick={() => { setSelectedDoc(doc); setEditNotes(doc.notes || doc.description || ''); }}
                  >
                    {canEdit && (
                      <td style={{ padding: '10px 8px', width: 36 }} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={isSelected}
                          onChange={e => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(doc.id); else next.delete(doc.id);
                            setSelectedIds(next);
                          }}
                          style={{ width: 15, height: 15, accentColor: '#4B3FFF', cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    <td style={{ padding: '10px 12px' }}>
                      <Group gap={8}>
                        <catInfo.icon size={16} color={catInfo.color} />
                        <Text size="sm" fw={500} style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320, whiteSpace: 'nowrap' }}>
                          {doc.title || '(untitled)'}
                        </Text>
                      </Group>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Badge
                        size="sm"
                        style={{
                          backgroundColor: `${catInfo.color}18`,
                          color: catInfo.color,
                          border: `1px solid ${catInfo.color}40`,
                        }}
                      >
                        {catInfo.label}
                      </Badge>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {(() => {
                        const invId = getLinkedInvoiceId(doc);
                        const invInfo = invId ? invoiceLookup.get(invId) : null;
                        if (!invInfo) return <Text size="xs" c="dimmed">—</Text>;
                        const cName = contractorLookup.get(invInfo.contractorId) || '';
                        return (
                          <div>
                            <Text size="xs" fw={500} c="#4B3FFF">{invInfo.number}</Text>
                            {cName && <Text size="xs" c="dimmed" style={{ fontSize: 11 }}>{cName}</Text>}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 13 }}>{doc.uploadedBy || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <Group gap={4} style={{ justifyContent: 'flex-end' }}>
                        <Button
                          variant="subtle" size="xs"
                          leftSection={<IconEye size={14} />}
                          loading={pdfLoading === doc.fileId}
                          disabled={!doc.fileId}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleView(doc); }}
                        >
                          {doc.fileId ? 'View' : 'No file'}
                        </Button>
                        {canEdit && (
                          <Button
                            variant="subtle" size="xs" color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDeleteTarget(doc); }}
                          >
                            Delete
                          </Button>
                        )}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── Document Detail Modal ── */}
      <Modal opened={!!selectedDoc} onClose={() => setSelectedDoc(null)} title="Document Detail" size="lg">
        {selectedDoc && (() => {
          const catInfo = getCategoryInfo(selectedDoc.documentType || selectedDoc.category);
          return (
            <Stack gap={12}>
              <SimpleGrid cols={2} spacing="sm">
                <div>
                  <Text size="xs" c="dimmed" fw={600}>Title</Text>
                  <Text fw={600}>{selectedDoc.title || '(untitled)'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed" fw={600}>Category</Text>
                  <Badge
                    style={{
                      backgroundColor: `${catInfo.color}18`,
                      color: catInfo.color,
                      border: `1px solid ${catInfo.color}40`,
                    }}
                  >
                    {catInfo.label}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed" fw={600}>Uploaded By</Text>
                  <Text size="sm">{selectedDoc.uploadedBy || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed" fw={600}>File ID</Text>
                  <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>{selectedDoc.fileId || '—'}</Text>
                </div>
              </SimpleGrid>

              {/* PDF viewer */}
              {selectedDoc.fileId && (
                <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
                  <Button
                    variant="light"
                    leftSection={<IconEye size={16} />}
                    loading={pdfLoading === selectedDoc.fileId}
                    onClick={() => handleView(selectedDoc)}
                    mb={8}
                  >
                    Open in New Tab
                  </Button>
                </div>
              )}

              {/* Notes */}
              <div style={{ borderTop: '1px solid #e8eaf0', paddingTop: 12 }}>
                <Text size="sm" fw={600} mb={4}>Notes / Annotations</Text>
                <Textarea
                  value={editNotes}
                  onChange={(e: any) => setEditNotes(e.target.value)}
                  minRows={3}
                  placeholder="Add notes about this document..."
                />
                {canEdit && (
                  <Button variant="light" size="xs" mt={8} onClick={handleSaveNotes}>
                    Save Notes
                  </Button>
                )}
              </div>
            </Stack>
          );
        })()}
      </Modal>

      {/* ── Upload Modal ── */}
      <Modal opened={showUpload} onClose={() => { setShowUpload(false); setUploadFiles([]); setUploadCategory(''); }} title="Upload Documents" size="lg">
        <Stack gap={12}>
          <Select
            label="Category"
            data={CATEGORIES.map(c => ({ value: c.value, label: c.label }))}
            value={uploadCategory}
            onChange={(v: string | null) => setUploadCategory(v || '')}
            placeholder="Select category..."
          />

          {/* Auto-classification hint */}
          {uploadFiles.length > 0 && uploadCategory && (
            <Group gap={8}>
              <Text size="xs" c="dimmed">Auto-detected:</Text>
              <Badge size="sm" style={{
                backgroundColor: `${getCategoryInfo(autoClassify(uploadFiles[0].name)).color}18`,
                color: getCategoryInfo(autoClassify(uploadFiles[0].name)).color,
              }}>
                {getCategoryInfo(autoClassify(uploadFiles[0].name)).label}
              </Badge>
              {autoClassify(uploadFiles[0].name) !== uploadCategory && (
                <Text size="xs" c="dimmed">(overridden)</Text>
              )}
            </Group>
          )}

          {/* Drop zone */}
          <div>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #d0d5dd',
                borderRadius: 8,
                padding: '32px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                background: '#fafbfc',
              }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#4B3FFF'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = '#d0d5dd'; }}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.style.borderColor = '#d0d5dd';
                const files = Array.from(e.dataTransfer.files);
                setUploadFiles(files);
                if (!uploadCategory && files.length > 0) {
                  setUploadCategory(autoClassify(files[0].name));
                }
              }}
            >
              <IconUpload size={32} color="#94a3b8" />
              <Text size="sm" c="dimmed" mt={4}>Click or drag files here</Text>
              <Text size="xs" c="dimmed">PDF, images, or any document type</Text>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </div>
          </div>

          {/* Selected files list */}
          {uploadFiles.length > 0 && (
            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              {uploadFiles.map((f, i) => (
                <Group key={i} gap={8} style={{ padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <IconFile size={14} color="#94a3b8" />
                  <Text size="sm" style={{ flex: 1 }}>{f.name}</Text>
                  <Text size="xs" c="dimmed">{formatFileSize(f.size)}</Text>
                </Group>
              ))}
            </div>
          )}

          {/* Progress */}
          {uploading && (
            <Paper withBorder p="sm" style={{ borderRadius: 6 }}>
              <Text size="sm">Uploading {uploadProgress.done} / {uploadProgress.total}...</Text>
              <div style={{
                height: 6, borderRadius: 3, background: '#e8eaf0', marginTop: 8,
              }}>
                <div style={{
                  height: '100%', borderRadius: 3, background: '#4B3FFF',
                  width: `${uploadProgress.total > 0 ? (uploadProgress.done / uploadProgress.total) * 100 : 0}%`,
                  transition: 'width 0.3s',
                }} />
              </div>
            </Paper>
          )}

          <Group style={{ justifyContent: 'flex-end' }} gap={8}>
            <Button variant="light" onClick={() => { setShowUpload(false); setUploadFiles([]); setUploadCategory(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              loading={uploading}
              disabled={uploadFiles.length === 0}
              leftSection={<IconUpload size={16} />}
            >
              Upload {uploadFiles.length} File(s)
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete confirmation ── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Document" size="sm">
        <Stack gap={12}>
          <Text size="sm">
            Are you sure you want to delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.
          </Text>
          <Group style={{ justifyContent: 'flex-end' }} gap={8}>
            <Button variant="light" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Fullscreen PDF viewer */}
      <Modal
        opened={!!pdfViewerUrl}
        onClose={() => { setPdfViewerUrl(null); setPdfViewerTitle(''); }}
        title={pdfViewerTitle}
        fullScreen
        styles={{ body: { padding: 0 }, header: { padding: '12px 20px' } }}
      >
        {pdfViewerUrl && (
          <iframe
            src={pdfViewerUrl}
            style={{ width: '100%', height: 'calc(100vh - 60px)', border: 'none', display: 'block' }}
            title={pdfViewerTitle}
          />
        )}
      </Modal>
    </Stack>
  );
}
